import { spacing, radii } from './foundation.js';

/**
 * Application layout tokens.
 *
 * These values define the application frame: header dimensions, page widths,
 * gutters, form/table dimensions, and responsive breakpoints. No component
 * should compute outer layout independently of these tokens.
 */
export const shell = {
  headerHeight: '64px',
  compactHeaderHeight: '56px',
  brandWidth: '200px',
  searchWidth: '220px',
  searchMinHeight: 38,
  accountMenuWidth: '360px',
  accountMenuMaxWidth: 'calc(100vw - 24px)',
  accountMenuMaxHeight: 'min(82vh, 720px)',
  controlGap: '8px',
  navigationBreakpoint: 1024,
  compactBreakpoint: 600,
} as const;

export const page = {
  gutter: {
    xs: '16px',
    sm: '16px',
    md: '24px',
    lg: '32px',
  },
  maxWidth: {
    narrow: '480px',
    standard: '1024px',
    wide: '1280px',
    full: '100%',
  },
  sectionGap: {
    compact: '16px',
    comfortable: '24px',
  },
  breadcrumbMaxWidth: 200,
} as const;

export const form = {
  // Business forms need enough room for naturally paired fields without
  // becoming a full-width reading surface. Keep this centralized so generic
  // and bespoke forms share one professional measure.
  maxWidth: '960px',
  sectionGap: '24px',
  fieldGap: '20px',
} as const;

export const auth = {
  cardMaxWidth: '440px',
  cardPadding: { xs: '16px', sm: '24px' },
  cardElevation: 3 as const,
  surfaceGap: '16px',
} as const;

export const table = {
  rowHeight: '48px',
  headerHeight: '48px',
  density: 'comfortable' as const,
  compactRowHeight: '40px',
} as const;

export const cardLayout = {
  padding: {
    none: 0,
    standard: `${spacing[2]}px`,
    comfortable: `${spacing[3]}px`,
  },
  borderRadius: `${radii.medium}px`,
  gap: `${spacing[2]}px`,
  minHeight: 180,
} as const;

export const dialog = {
  padding: `${spacing[3]}px`,
  gap: `${spacing[2]}px`,
  maxWidth: {
    xs: '100%',
    sm: '560px',
    md: '720px',
  },
} as const;

export const toolbar = {
  height: '64px',
  compactHeight: '48px',
  gap: `${spacing[1]}px`,
} as const;

export const viewport = {
  compactPhone: 0,
  largePhone: 375,
  tablet: 768,
  smallDesktop: 1024,
  desktop: 1280,
  wideDesktop: 1440,
} as const;
